import "./SubmissionSection.css";
import { Button, Checkbox, CircularProgress, createTheme, FormControlLabel, MenuItem, TextField, ThemeProvider } from "@mui/material";
import React from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";

const APP_URL = "https://script.google.com/macros/s/AKfycbxQdgPnb65v63DFw2ei1DrSvatNrZYIMfQmJQEHS7Jss_S1q8aZhGuTY7_A3CJy3UBcZA/exec";

const theme = createTheme({
    palette: {
        primary: {
            main: "#5f9ea0"
        },
        secondary: {
            main: "#2f4f4f"
        },
    },
    typography: {
        fontFamily: "Lexend"
    },
    components: {
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    cursor: "none",
                    backgroundColor: "#8c8782",
                    "&:hover": {
                        backgroundColor: "#b8b3aa",
                        "@media (hover: none)": {
                            backgroundColor: "#b8b3aa"
                        }
                    },
                    "&.Mui-focused": {
                        backgroundColor: "#f9f0e8"
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    cursor: "none"
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    cursor: "none",
                    color: "var(--color-i)"
                }
            }
        }
    }
});

export default function SubmissionSection(props: {
    cursorLinkEnter: React.MouseEventHandler<HTMLDivElement> | undefined;
    cursorEnter: React.MouseEventHandler<HTMLDivElement> | undefined;
    cursorLeave: React.MouseEventHandler<HTMLDivElement> | undefined;
}) {
    const [email, setEmail] = React.useState('');
    const [type, setType] = React.useState('');
    const [description, setDescriptions] = React.useState('');
    const [references, setReferences] = React.useState('');
    const [postPerm, setPostPerm] = React.useState("No");
    const [missingTypeError, setMissingTypeError] = React.useState(false);
    const [formLoading, setFormLoading] = React.useState(false);
    const [formSuccess, setFormSuccess] = React.useState(false);
    const [formError, setFormError] = React.useState(false);
    const [fileHover, setFileHover] = React.useState(false);
    const [queueRequests, setQueueRequests] = React.useState(0);

    function getRequests() {
        axios.get(`${APP_URL}`)
            .then(function (response) {
                // console.log(response);
                setQueueRequests(response.data);
            })
            .catch(function (error) {
                // console.log(error);
            })
    }

    React.useEffect(() => {
        getRequests();
    }, []);

    function sendForm() {
        if (type === '') {
            setMissingTypeError(true);
            return;
        }

        setFormLoading(true);

        const headers = {
            'Content-Type': "multipart/form-data"
        }

        const body = {
            Contact: email,
            Type: type,
            Postable: postPerm,
            Description: description,
            References: references
        }

        axios.post(`${APP_URL}`, body, { headers: headers })
            .then(function (response) {
                // console.log(response);
                setFormSuccess(true);
                setFormError(false);
                getRequests();
            })
            .catch(function (error) {
                // console.log(error);
                setFormError(true);
            })
            .finally(function () {
                setFormLoading(false);
            });
    }

    return (
        <>
            <div className="submission-section-container"
                onMouseEnter={props.cursorEnter}
                onMouseLeave={props.cursorLeave} >
                <div className="title-container">
                    <h1>SUBMISSION FORM</h1>
                </div>
                <div className="inputs-container">
                    <AnimatePresence>
                        {
                            (formLoading || formSuccess) && <motion.div className="loading-container"
                                initial={{ height: 0 }}
                                animate={{ height: "90%" }}
                                exit={{ height: 0 }}
                                key="loading-container"
                            >

                                <AnimatePresence initial={true}>
                                    {
                                        formLoading ? <CircularProgress aria-label="Loading..." sx={{ marginTop:"-15%", position: "absolute", color: "var(--color-p)" }} size="10%" />
                                            : <motion.div className="thank-you-message-container"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 100 }}
                                                exit={{ opacity: 0 }}
                                                key="thank-you">
                                                <p>Thank you for your submission!</p>
                                                <p>I'll be in touch when I can: please be patient as it is first come first serve.</p>
                                            </motion.div>
                                    }
                                </AnimatePresence>
                            </motion.div>
                        }
                    </AnimatePresence>
                    <div className="inputs" >
                        <ThemeProvider theme={theme}>
                            <TextField value={email} id="email" label="Email / Contact" variant="filled" onChange={(e) => setEmail(e.target.value)} sx={{ minWidth: "80vw", maxWidth: "80vw" }} />
                            <TextField error={missingTypeError} id="type" value={type} label={"Type"} variant="filled" select onChange={(e) => { setType(e.target.value); setMissingTypeError(false); }} sx={{ minWidth: "80vw", maxWidth: "80vw" }}>
                                <MenuItem value="Sketch" sx={{ cursor: "none" }}>Sketch</MenuItem>
                                <MenuItem value="Colour" sx={{ cursor: "none" }}>Colour</MenuItem>
                                <MenuItem value="BG + Render" sx={{ cursor: "none" }}>BG + Render</MenuItem>
                            </TextField>
                            <TextField value={references} id="references" label="Link to references (Google Drive or Dropbox folder preferable)" variant="filled" onChange={(e) => setReferences(e.target.value)} sx={{ minWidth: "80vw", maxWidth: "80vw" }} />
                            <TextField value={description} label={"Description - Please provide details like posing, composition, etc. if possible"} id="details" multiline variant="filled" minRows={15} maxRows={15} onChange={(e) => setDescriptions(e.target.value)} sx={{ minWidth: "80vw" }} />
                            <FormControlLabel control={<Checkbox onChange={() => { postPerm === "Yes" ? setPostPerm("No") : setPostPerm("Yes") }} />} label="Are you okay with this drawing being posted to my socials? (Checking this doesn't guarantee that it will be)" sx={{ color: "var(--color-p)", cursor: "none" }} />
                        </ThemeProvider>

                        <div onMouseEnter={() => setFileHover(true)} onMouseLeave={() => setFileHover(false)} className={fileHover ? "dropzone-file-list-container" : "dropzone-file-list-container-hover"}>
                            {!formError ?
                                <span>
                                    <p>Please provide as much reference as possible! I'll follow up if I need more.</p>
                                    <p>For other work inquiries, contact spoogyoogie@gmail.com.</p>
                                </span> :
                                <span>
                                    <p>Error: couldn't contact server.</p>
                                </span>
                            }
                        </div>

                        <div className="submit-button-container">
                            <Button onClick={() => sendForm()} variant="contained" sx={{
                                cursor: "none", backgroundColor: "var(--color-h1)", boxShadow: 0,
                                '&:hover': {
                                    cursor: "none",
                                    backgroundColor: '#f9f0e8',
                                    borderWidth: "2px",
                                    borderColor: "#f9f0e8",
                                    color: "#1f1e1c"
                                }, fontFamily: "Staatliches", width: "100%", fontSize: "xx-large",
                            }}>SUBMIT</Button>
                        </div>
                    </div>
                </div>
                <div className="queue-preview-section-container" >
                    <div className="queue-info">REQUESTS IN QUEUE:</div>
                    <div className="queue-preview">{queueRequests}</div>
                </div>
            </div>
        </>
    );
}

    // function DeleteFile(file: File) {
    //     console.log("deleting: " + file.name);
    //     setFiles(files.filter(f => f !== file));
    // }

    // function ReadFiles() {
    //     if (files.length > 0) {
    //         let promises = [];
    //         let stringFiles = [];
    //         files.forEach((file) => {
    //             promises.push(new Promise((resolve, reject) => {
    //                 const reader = new FileReader();
    //                 reader.readAsDataURL(file);
    //                 reader.onload = () => {
    //                     resolve(reader.result);
    //                 };
    //                 reader.onerror = (error) => {
    //                     reject(error);
    //                 };
    //             })
    //             );
    //         });
    //     }
    // }

/*
FILE DROP (deprecated)
<div className="dropzone-file-list-container" >
                        {files.length === 0 && <p>Please give as much reference as possible! I'll follow up in the event I need more.</p>}
                        {files.map((file) => <FilePreview file={file} onDelete={DeleteFile} />)}
                    </div>

<div onMouseEnter={() => setFileHover(true)} onMouseLeave={() => setFileHover(false)} className={dropError ? "dropzone-error" : (fileHover ? "dropzone-hover" : "dropzone")} >
                        <Dropzone maxFiles={50} maxSize={25000000} onDropRejected={() => setDropError(true)} onError={() => setDropError(true)} onDrop={acceptedFiles => {
                            console.log(acceptedFiles)
                            const allFiles = [...files, ...acceptedFiles];
                            console.log(allFiles);
                            if (allFiles.length > 50) {
                                setDropError(true);
                            } else {
                                setFiles(allFiles);
                                setDropError(false);
                            }
                        }}>
                            {({ getRootProps, getInputProps }) => (
                                <section style={{ width: "100%" }}>
                                    <div  {...getRootProps()} >
                                        <input {...getInputProps()} />
                                        <div className="file-upload-area">
                                            <div className="dropzone-label-container">
                                                {dropError ? <p>Error (max 50 files)</p> : <p>Drag or click here to upload any files (References, etc.)</p>}
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}
                        </Dropzone>
                    </div>
                    */